import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import { sustituirPlaceholdersDocumentacion } from "../../utils/documentacionPlaceholders";
import usePermiso from "../../hooks/usePermiso";

async function cargarHtmlPlantilla(archivo) {
  const res = await fetch(archivo);
  if (!res.ok) throw new Error(`No se pudo cargar la plantilla (HTTP ${res.status})`);
  let html = await res.text();
  html = await sustituirPlaceholdersDocumentacion(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelector(".screen-label")?.remove();
  doc.querySelector(".mode-switch")?.remove();

  const viewerCss = doc.createElement("style");
  viewerCss.textContent = `
    html, body {
      overflow: auto !important;
      height: auto !important;
      min-height: 100%;
    }
    @media print {
      html, body {
        padding: 0 !important;
        margin: 0 !important;
        min-height: 0 !important;
        height: auto !important;
        display: block !important;
        background: white !important;
        overflow: visible !important;
      }
      img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  `;
  doc.head.appendChild(viewerCss);

  return "<!DOCTYPE html>" + doc.documentElement.outerHTML;
}

export default function VisorPlantillaImpresion({ plantilla, onClose }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const verImprimir = puede("pla_imp_previsualizar", "ver");
  const iframeRef = useRef(null);
  const blobUrlRef = useRef("");

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [blobUrl, setBlobUrl] = useState("");

  const revocarBlob = useCallback(() => {
    if (blobUrlRef.current) {
      try {
        URL.revokeObjectURL(blobUrlRef.current);
      } catch {
        /* ignore */
      }
      blobUrlRef.current = "";
    }
  }, []);

  useEffect(() => {
    if (!plantilla?.archivo) return undefined;

    let cancelado = false;
    setCargando(true);
    setError("");
    setBlobUrl("");
    revocarBlob();

    (async () => {
      try {
        const html = await cargarHtmlPlantilla(plantilla.archivo);
        if (cancelado) return;
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      } catch (e) {
        if (!cancelado) setError(e?.message || "Error cargando la plantilla");
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();

    return () => {
      cancelado = true;
      revocarBlob();
    };
  }, [plantilla?.archivo, revocarBlob]);

  useEffect(() => {
    if (!plantilla) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [plantilla, onClose]);

  const imprimir = () => {
    const frameWin = iframeRef.current?.contentWindow;
    if (!frameWin) return;
    try {
      frameWin.focus();
      frameWin.print();
    } catch {
      /* ignore */
    }
  };

  if (!plantilla) return null;

  const titulo = plantilla.label || "Plantilla";
  const subtitulo = [plantilla.formato, plantilla.orientacion].filter(Boolean).join(" · ");

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12000,
        background: "rgba(0,0,0,0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "12px 16px",
        fontFamily: "inherit",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !cargando) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Vista previa: ${titulo}`}
    >
      <div
        style={{
          width: "min(96vw, 1100px)",
          height: "min(92vh, 900px)",
          display: "flex",
          flexDirection: "column",
          borderRadius: 12,
          overflow: "hidden",
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.55)" : "0 20px 60px rgba(0,0,0,0.28)",
          color: c.textPrimary,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 14px",
            borderBottom: `1px solid ${c.border}`,
            background: c.bgPanel || c.bgCard,
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={titulo}
            >
              {plantilla.emoji ? `${plantilla.emoji} ` : ""}
              {titulo}
            </div>
            {subtitulo && (
              <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>{subtitulo}</div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {verImprimir && (
            <button
              type="button"
              onClick={imprimir}
              disabled={cargando || !!error || !blobUrl}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                border: "none",
                cursor: cargando || !!error || !blobUrl ? "not-allowed" : "pointer",
                background: plantilla.color || "#c8002a",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                opacity: cargando || !!error || !blobUrl ? 0.5 : 1,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              Imprimir
            </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "7px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                cursor: "pointer",
                background: c.bgInput,
                color: c.textPrimary,
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              Cerrar
            </button>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 8,
            background: isDark ? "#141820" : "#e8ecf1",
          }}
        >
          {cargando && (
            <div style={{ fontSize: 13, color: c.textMuted, textAlign: "center", padding: 20 }}>
              Cargando plantilla…
            </div>
          )}
          {!cargando && error && (
            <div style={{ fontSize: 13, color: "#ef4444", textAlign: "center", padding: 20, maxWidth: 420 }}>
              {error}
            </div>
          )}
          {!cargando && !error && blobUrl && (
            <iframe
              ref={iframeRef}
              title={`Vista previa ${titulo}`}
              src={blobUrl}
              style={{
                width: "100%",
                height: "100%",
                border: `1px solid ${c.border}`,
                borderRadius: 6,
                background: "#fff",
              }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
